import { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Box, Typography, FormHelperText, Grid2, Skeleton, CircularProgress } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { useForm } from '@carcareers/contexts';
import {
  AddPaymentModal,
  CheckBoxInput,
  DateInput,
  Payment,
  PaymentCard,
  TextInput,
  LocationInput,
} from '@carcareers/components';
import { paymentApi } from '@carcareers/store';

export const PaymentStepForm: FC = () => {
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs(startDate).add(30, 'day'));

  const { setData, getErrors, data } = useForm();
  const paymentProfileErrors = getErrors('payment.customerPaymentProfileId');
  const generalPaymentErrors = getErrors('payment');
  const { data: paymentData, isLoading } = paymentApi.useGetCustomerProfileQuery();

  // Check if user has existing payment profiles
  const hasPaymentProfiles = paymentData?.profile?.paymentProfiles?.length
    ? paymentData.profile.paymentProfiles.length > 0
    : false;

  useEffect(() => {
    if (data?.startDate) {
      setStartDate(dayjs(data.startDate));
      setEndDate(dayjs(data.startDate).add(30, 'day'));
    }
  }, [data?.startDate]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body1">Loading payment information...</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="text" width={300} height={32} />
          <Skeleton variant="text" width={400} height={24} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton variant="rounded" width={200} height={120} />
            <Skeleton variant="rounded" width={200} height={120} />
          </Box>
          <Skeleton variant="rounded" width={300} height={56} />
          <Skeleton variant="rounded" width={300} height={56} />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h4">
        Pay CARCAREERS.COM{' '}
        <Typography component="span" fontWeight="bold">
          $329
        </Typography>{' '}
        for 30 days.
      </Typography>
      <Typography variant="body1">
        Your job posting will start on {startDate.format('MM/DD/YYYY')} and end on {endDate.format('MM/DD/YYYY')}.
      </Typography>
      {generalPaymentErrors && (
        <FormHelperText error sx={{ fontSize: '0.95rem', mt: 1 }}>
          {generalPaymentErrors}
        </FormHelperText>
      )}
      {hasPaymentProfiles ? (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Payment
              isInput
              fieldName="payment.customerPaymentProfileId"
              onClick={(paymentId) => setData('payment.customerPaymentProfileId', Number(paymentId))}
            />
            <PaymentCard isNew onClick={() => setIsAddPaymentModalOpen(true)} />
          </Box>
          {paymentProfileErrors && <FormHelperText error>Please select a payment method.</FormHelperText>}
        </>
      ) : (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payment Information
          </Typography>
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12 }}>
              <TextInput label="Card Number" name="payment.cardNumber" required fullWidth />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <DatePicker
                views={['year', 'month']}
                label="Expiration"
                minDate={dayjs(new Date())}
                onChange={(value) => setData('payment.expirationDate', dayjs(value).format('MM/YY'))}
                format="MM/YY"
                sx={{ width: '100%' }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextInput label="Security Code" name="payment.cardCode" required fullWidth />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextInput label="First Name" name="payment.firstName" required fullWidth />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextInput label="Last Name" name="payment.lastName" required fullWidth />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <LocationInput label="Billing Address" name="payment.address" required fullWidth />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <CheckBoxInput label="Set as default payment method" name="payment.defaultPaymentProfile" />
            </Grid2>
          </Grid2>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: 'column', maxWidth: 300 }}>
        <Typography variant="h6">Start Date</Typography>
        <DateInput name="startDate" label="Start Date" minDate={dayjs(today)} />
        <Typography variant="h6">Promo Code</Typography>
        <TextInput name="payment.promoCode" label="Promo Code" />
      </Box>
      <CheckBoxInput name="payment.renew" label="Auto Renew" />
      {hasPaymentProfiles && (
        <AddPaymentModal open={isAddPaymentModalOpen} onClose={() => setIsAddPaymentModalOpen(false)} />
      )}
    </>
  );
};
